import { useEffect, useState } from 'react'
import { AlertTriangle, CheckCircle2, Loader2, Plus, RefreshCw, Trash2 } from 'lucide-react'
import { useAddBagEntry, useDeleteBagEntry, useGetBagStatus } from '../../hooks/backend/bagsPage'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../lib/shadcn/card'
import { Badge } from '../../lib/shadcn/badge'
import { Button } from '../../lib/shadcn/button'
import { Input } from '../../lib/shadcn/input'
import { Label } from '../../lib/shadcn/label'
import { NativeSelect } from '../../lib/shadcn/native-select'
import { toast } from '../../lib/shadcn/sonner'
import { cn } from '../../lib/shadcn/utils'
import {
  bags,
  formatDay,
  localToday,
  notifyBagStatusChanged,
  type BagSize,
  type BagSizeStatus,
  type BagStatus,
} from '../../utils/bagStatus'
import { HelpText } from '../../components/HelpText'

function SizeCard({ s }: { s: BagSizeStatus }) {
  if (!s.tracking) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{s.size} bags</CardTitle>
          <CardDescription>
            Not tracked yet. Log a delivery or a stock count below to start.
          </CardDescription>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          Using about {s.avg_daily_use.toLocaleString('en-US')} a day. Suggested reorder point:{' '}
          {bags(s.suggested_reorder_point)}.
        </CardContent>
      </Card>
    )
  }
  return (
    <Card className={cn(s.needs_reorder && 'border-warning')}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-2">
          <CardTitle>{s.size} bags</CardTitle>
          {s.needs_reorder ? (
            <Badge variant="warning" className="gap-1">
              <AlertTriangle className="h-3 w-3" /> Reorder now
            </Badge>
          ) : (
            <Badge variant="success" className="gap-1">
              <CheckCircle2 className="h-3 w-3" /> OK
            </Badge>
          )}
        </div>
        <CardDescription>
          Counting from {formatDay(s.start_date)}: {bags(s.received_since_start)} in,{' '}
          {bags(s.used_since_start)} ordered.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <div
            className={cn(
              'text-4xl font-bold tabular-nums',
              s.on_hand < 0 && 'text-destructive',
              s.needs_reorder && s.on_hand >= 0 && 'text-warning'
            )}
          >
            {s.on_hand.toLocaleString('en-US')}
          </div>
          <div className="text-sm text-muted-foreground">on hand</div>
        </div>
        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
          <dt className="text-muted-foreground">Reorder point</dt>
          <dd className="tabular-nums">
            {s.reorder_point.toLocaleString('en-US')}
            <span className="text-muted-foreground">
              {s.reorder_point_override === null
                ? ' (suggested)'
                : ` (yours; suggested ${s.suggested_reorder_point.toLocaleString('en-US')})`}
            </span>
          </dd>
          <dt className="text-muted-foreground">Avg use (30 days)</dt>
          <dd className="tabular-nums">{s.avg_daily_use.toLocaleString('en-US')} / day</dd>
          <dt className="text-muted-foreground">Lead time</dt>
          <dd className="tabular-nums">{s.lead_time_days} days</dd>
          <dt className="text-muted-foreground">Reorder by</dt>
          <dd className={cn(s.needs_reorder && 'font-semibold text-warning')}>
            {s.needs_reorder ? 'Now' : formatDay(s.reorder_by)}
          </dd>
          <dt className="text-muted-foreground">Runs out around</dt>
          <dd>
            {s.days_left === null ? '—' : `${formatDay(s.out_by)} (${s.days_left} days)`}
          </dd>
        </dl>
      </CardContent>
    </Card>
  )
}

export default function BagsPanel() {
  const getBagStatus = useGetBagStatus()
  const addBagEntry = useAddBagEntry()
  const deleteBagEntry = useDeleteBagEntry()
  const status = getBagStatus.data as BagStatus | null | undefined

  const [size, setSize] = useState<BagSize>('Small')
  const [kind, setKind] = useState<'received' | 'count'>('received')
  const [quantity, setQuantity] = useState('')
  const [day, setDay] = useState(localToday())
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState<string | null>(null)

  useEffect(() => {
    void getBagStatus.trigger({}, { skipCache: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const refresh = async () => {
    await getBagStatus.trigger({}, { skipCache: true })
    notifyBagStatusChanged()
  }

  const save = async () => {
    const qty = Number(quantity)
    if (!quantity.trim() || !Number.isInteger(qty) || qty < 0) {
      toast.error('Enter a whole number of bags')
      return
    }
    if (!day) {
      toast.error('Pick a date')
      return
    }
    try {
      setBusy('add')
      await addBagEntry.trigger({ size, kind, quantity: qty, received_on: day, note })
      setQuantity('')
      setNote('')
      await refresh()
      toast.success(
        kind === 'count' ? `${size} bag count set to ${qty}` : `Logged ${bags(qty)} received (${size})`
      )
    } catch {
      toast.error('Failed to save')
    } finally {
      setBusy(null)
    }
  }

  const remove = async (id: number) => {
    try {
      setBusy(`del-${id}`)
      await deleteBagEntry.trigger({ id })
      await refresh()
    } catch {
      toast.error('Failed to delete entry')
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <p className="text-sm text-muted-foreground">
          Bags on hand by size, counting down as orders come in.
        </p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => void refresh()}
          disabled={getBagStatus.loading}
        >
          <RefreshCw className={cn('w-4 h-4 mr-2', getBagStatus.loading && 'animate-spin')} />
          Refresh
        </Button>
      </div>

      {getBagStatus.error && !status && (
        <div className="flex items-center gap-2 p-3 rounded-md bg-destructive/10 text-destructive text-sm">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          {getBagStatus.error}
        </div>
      )}

      {!status && !getBagStatus.error ? (
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      ) : status ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {status.sizes.map(s => (
            <SizeCard key={s.size} s={s} />
          ))}
        </div>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Log bags</CardTitle>
          <CardDescription><HelpText>
            A delivery adds to what's on hand. A stock count replaces it with what you counted on
            the shelf. Orders dated on or after that day are subtracted.
          </HelpText></CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <div className="space-y-1.5">
              <Label htmlFor="bag-size">Size</Label>
              <NativeSelect
                id="bag-size"
                value={size}
                onChange={e => setSize(e.target.value as BagSize)}
                className="h-9"
              >
                <option value="Small">Small</option>
                <option value="Large">Large</option>
              </NativeSelect>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="bag-kind">Type</Label>
              <NativeSelect
                id="bag-kind"
                value={kind}
                onChange={e => setKind(e.target.value as 'received' | 'count')}
                className="h-9"
              >
                <option value="received">Delivery received</option>
                <option value="count">Stock count</option>
              </NativeSelect>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="bag-qty">Bags</Label>
              <Input
                id="bag-qty"
                type="number"
                min="0"
                step="1"
                inputMode="numeric"
                value={quantity}
                onChange={e => setQuantity(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); void save() } }}
                className="h-9"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="bag-date">Date</Label>
              <Input
                id="bag-date"
                type="date"
                value={day}
                onChange={e => setDay(e.target.value)}
                className="h-9"
              />
            </div>
          </div>
          <div className="flex items-end gap-3">
            <div className="min-w-0 flex-1 space-y-1.5">
              <Label htmlFor="bag-note">Note</Label>
              <Input
                id="bag-note"
                value={note}
                onChange={e => setNote(e.target.value)}
                placeholder="Optional"
                className="h-9"
              />
            </div>
            <Button
              type="button"
              onClick={() => void save()}
              disabled={busy === 'add' || !quantity.trim()}
              className="gap-1.5 shrink-0"
            >
              {busy === 'add' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              Save
            </Button>
          </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>History</CardTitle>
          <CardDescription>Your most recent deliveries and counts.</CardDescription>
        </CardHeader>
        <CardContent>
          {!status || status.entries.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nothing logged yet.</p>
          ) : (
            <div className="divide-y rounded-md border">
              {status.entries.map(e => (
                <div key={e.id} className="flex items-center gap-3 px-3 py-2 text-sm">
                  <span className="w-28 shrink-0">{formatDay(e.received_on)}</span>
                  <span className="w-14 shrink-0 font-medium">{e.size}</span>
                  <span className="w-36 shrink-0 text-muted-foreground">
                    {e.kind === 'count' ? 'Stock count' : 'Delivery received'}
                  </span>
                  <span className="w-24 shrink-0 tabular-nums">
                    {e.kind === 'count' ? '=' : '+'}
                    {e.quantity.toLocaleString('en-US')}
                  </span>
                  <span className="flex-1 truncate text-muted-foreground">{e.note}</span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-muted-foreground hover:text-destructive"
                    onClick={() => void remove(e.id)}
                    disabled={busy === `del-${e.id}`}
                    aria-label="Delete entry"
                  >
                    {busy === `del-${e.id}` ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
